import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const AUTO_REDIRECT_TO_KEYCLOAK = true;

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const { t } = useTranslation(['common', 'auth']);

  useEffect(() => {
    if (!loading && !user && AUTO_REDIRECT_TO_KEYCLOAK) {
      login('oidc');
    }
  }, [loading, user, login]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-4">
          <Skeleton className="w-full h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
      <div className="w-full max-w-md relative z-10 mx-4">
        <Card className="shadow-sm">
          <CardHeader className="text-center space-y-2 pb-2">
            <img
              src="/logo.svg"
              alt={t('common:appName')}
              className="h-12 w-12 mx-auto"
            />
            <CardTitle className="text-2xl font-semibold font-heading tracking-tight">
              {t('common:appName')}
            </CardTitle>
            <CardDescription>{t('auth:tagline')}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {AUTO_REDIRECT_TO_KEYCLOAK && (
              <p className="text-sm text-center text-muted-foreground">
                {t('auth:redirecting')}
              </p>
            )}
            <Button
              variant="default"
              className="w-full h-12"
              onClick={() => login('oidc')}
            >
              {t('auth:continueKeycloak')}
            </Button>
            <Button
              variant="outline"
              className="w-full h-12"
              onClick={() => login('google')}
            >
              <svg
                className="mr-2 h-5 w-5"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('auth:continueGoogle')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
